import React, { useState, useEffect, useRef } from 'react';
import { 
  getBrowserVoiceWsUrl, 
  getBrowserVoiceKokoroWsUrl,
  getBrowserVoiceKokoroVadWsUrl,
  getBrowserVoiceKokoroWsTtsUrl,
  getCurrentUserIdForCalls,
} from '../lib/apiConfig';

/**
 * Browser Voice Agent Widget
 * 
 * Ultra-low latency voice agent component for React applications
 * Integrates with BrowserVoiceAgent.js backend
 * 
 * Usage:
 * <BrowserVoiceWidget 
 *   wsUrl="ws://localhost:3010/browser-voice-agent"
 *   scriptId="your_script_id"
 *   onTranscript={(role, text) => console.log(role, text)}
 *   onMetrics={(metrics) => console.log(metrics)}
 * />
 */

const BrowserVoiceWidget = ({
  wsUrl = null, // Will be auto-determined from backendMode if not provided
  scriptId = null,
  agentId = null, // Agent-based: the server resolves script + KB + voice from this agent.
  collectionIds = null,
  voiceId = null,
  firstMessage = "Hi! I'm Maya, your AI assistant. How can I help you today?",
  systemPrompt = 'You are Maya, a helpful AI assistant.',
  agentName = null,
  onTranscript = null,
  onMetrics = null,
  onStatusChange = null,
  autoStart = false,
  showTranscript = true,
  showMetrics = true,
  showVisualizer = true,
  backendMode = 'legacy',
  realtimeTokenUrl = '/realtime-token',
  extraStart = null, // extra fields merged into the WS 'start' (e.g. Voice Lab: {language, voiceCfg})
}) => {
  // Auto-determine wsUrl based on backendMode if not explicitly provided
  const resolvedWsUrl = wsUrl || (() => {
    switch (backendMode) {
      case 'kokoro-ws':
        return getBrowserVoiceKokoroWsTtsUrl();
      case 'kokoro-vad-ws':
        return getBrowserVoiceKokoroWsTtsUrl();
      case 'kokoro-vad':
        return getBrowserVoiceKokoroVadWsUrl();
      case 'kokoro':
        return getBrowserVoiceKokoroWsUrl();
      default:
        return getBrowserVoiceWsUrl();
    }
  })();
  const [status, setStatus] = useState('disconnected'); // disconnected, connecting, ready, listening, processing, speaking
  const [transcript, setTranscript] = useState([]);
  const [metrics, setMetrics] = useState({ detection: 0, kb: 0, gpt: 0, tts: 0, total: 0 });
  const [isRecording, setIsRecording] = useState(false);
  const [isPttRecording, setIsPttRecording] = useState(false); // Push-to-talk recording state
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);

  const wsRef = useRef(null);
  const realtimeWsRef = useRef(null);
  const peerRef = useRef(null);
  const dataChannelRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioProcessorRef = useRef(null);
  const analyserRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const activeUserUtteranceIndexRef = useRef(null); // merge partial user transcripts
  const isPttRecordingRef = useRef(false); // Push-to-talk recording state ref
  const pttAudioQueueRef = useRef([]); // PTT audio chunk queue
  const pttIsPlayingRef = useRef(false); // PTT playback state
  const pttPlaybackCtxRef = useRef(null); // Single playback context for PTT

  // Update status and notify parent
  const updateStatus = (newStatus) => {
    setStatus(newStatus);
    if (onStatusChange) onStatusChange(newStatus);
  };

  // PTT audio queue playback - plays chunks sequentially
  const playPttAudioQueue = async () => {
    if (pttIsPlayingRef.current) return; // Already playing
    if (pttAudioQueueRef.current.length === 0) return; // Nothing to play

    pttIsPlayingRef.current = true;

    // Get or create playback context
    if (!pttPlaybackCtxRef.current || pttPlaybackCtxRef.current.state === 'closed') {
      pttPlaybackCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const playNext = async () => {
      if (pttAudioQueueRef.current.length === 0) {
        pttIsPlayingRef.current = false;
        return;
      }

      const audioData = pttAudioQueueRef.current.shift();
      try {
        const audioBuffer = await pttPlaybackCtxRef.current.decodeAudioData(audioData.slice(0)); // slice to clone
        const source = pttPlaybackCtxRef.current.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(pttPlaybackCtxRef.current.destination);
        source.onended = () => {
          playNext();
        };
        source.start(0);
      } catch (err) {
        console.error('PTT audio decode error:', err);
        playNext(); // Skip this chunk and try next
      }
    };

    playNext();
  };

  // Add transcript entry
  const addTranscript = (role, text) => {
    const entry = { role, text, timestamp: Date.now() };
    setTranscript(prev => [...prev, entry]);
    if (onTranscript) onTranscript(role, text);
  };

  // Merge or append user transcript to avoid double messages for the same utterance
  const upsertUserTranscript = (text) => {
    setTranscript(prev => {
      if (activeUserUtteranceIndexRef.current == null) {
        const next = [...prev, { role: 'user', text, timestamp: Date.now() }];
        activeUserUtteranceIndexRef.current = next.length - 1;
        if (onTranscript) onTranscript('user', text);
        return next;
      } else {
        const idx = activeUserUtteranceIndexRef.current;
        const next = [...prev];
        next[idx] = { ...next[idx], text, timestamp: Date.now() };
        if (onTranscript) onTranscript('user', text);
        return next;
      }
    });
  };

  const finalizeUserUtterance = () => {
    activeUserUtteranceIndexRef.current = null;
  };

  // Update metrics
  const updateMetrics = (newMetrics) => {
    setMetrics(newMetrics);
    if (onMetrics) onMetrics(newMetrics);
  };

  const stopRealtime = async () => {
    setIsRecording(false);
    setIsAssistantSpeaking(false);
    
    // Send stop message to backend
    if (realtimeWsRef.current && realtimeWsRef.current.readyState === WebSocket.OPEN) {
      realtimeWsRef.current.send(JSON.stringify({ type: 'stop' }));
    }
    
    // Close Realtime WebSocket
    if (realtimeWsRef.current) {
      realtimeWsRef.current.close();
      realtimeWsRef.current = null;
    }
    
    // Stop audio processor
    if (audioProcessorRef.current) {
      audioProcessorRef.current.disconnect();
      audioProcessorRef.current = null;
    }
    
    // Close audio context
    if (audioContextRef.current) {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    
    // Stop visualization
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  // Convert ArrayBuffer to base64
  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  // Convert base64 to ArrayBuffer
  const base64ToArrayBuffer = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  // Play realtime audio chunks (PCM16, 24kHz)
  const playRealtimeAudio = async (audioCtx, queue) => {
    if (isPlayingRef.current || queue.length === 0) return;
    
    isPlayingRef.current = true;
    const audioData = queue.shift();
    
    try {
      // Convert PCM16 to Float32 for Web Audio API
      const pcm16 = new Int16Array(audioData);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768.0;
      }
      
      const audioBuffer = audioCtx.createBuffer(1, float32.length, 24000);
      audioBuffer.copyToChannel(float32, 0);
      
      const source = audioCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioCtx.destination);
      
      source.onended = () => {
        isPlayingRef.current = false;
        if (queue.length > 0) {
          playRealtimeAudio(audioCtx, queue);
        } else {
          updateStatus('listening');
        }
      };
      
      source.start(0);
    } catch (error) {
      console.error('Realtime audio playback error:', error);
      isPlayingRef.current = false;
      if (queue.length > 0) {
        playRealtimeAudio(audioCtx, queue);
      }
    }
  };

  // Play audio queue
  const playAudioQueue = async () => {
    if (backendMode === 'realtime') {
      return;
    }
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      updateStatus('listening');
      return;
    }

    isPlayingRef.current = true;
    const audioData = audioQueueRef.current.shift();
    
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 22050 });
      const audioBuffer = await audioCtx.decodeAudioData(audioData);
      const source = audioCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioCtx.destination);
      
      source.onended = () => {
        playAudioQueue();
      };
      
      source.start(0);
    } catch (error) {
      console.error('Audio playback error:', error);
      playAudioQueue();
    }
  };

  // Start voice agent
  const startLegacy = async () => {
    try {
      updateStatus('connecting');

      // Get microphone access
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Setup audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ 
        sampleRate: 16000 
      });
      const source = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
      
      // Setup analyser for visualization
      if (showVisualizer) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        source.connect(analyserRef.current);
        startVisualization();
      }

      // Setup audio processor
      const workletCode = `
        class PCMProcessor extends AudioWorkletProcessor {
          process(inputs) {
            const input = inputs[0];
            if (input.length > 0) {
              const channelData = input[0];
              const pcm16 = new Int16Array(channelData.length);
              for (let i = 0; i < channelData.length; i++) {
                const s = Math.max(-1, Math.min(1, channelData[i]));
                pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
              }
              this.port.postMessage(pcm16.buffer);
            }
            return true;
          }
        }
        registerProcessor('pcm-processor', PCMProcessor);
      `;
      
      const blob = new Blob([workletCode], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);
      
      await audioContextRef.current.audioWorklet.addModule(workletUrl);
      audioProcessorRef.current = new AudioWorkletNode(audioContextRef.current, 'pcm-processor');
      source.connect(audioProcessorRef.current);

      // Connect WebSocket
      console.log('[BrowserVoiceWidget] Connecting to WebSocket:', resolvedWsUrl);
      console.log('[BrowserVoiceWidget] Backend mode:', backendMode);
      wsRef.current = new WebSocket(resolvedWsUrl);

      wsRef.current.onopen = () => {
        console.log('✅ WebSocket connected to:', resolvedWsUrl);
        updateStatus('ready');

        // Send start configuration
        const selectedBackendMode = backendMode === 'realtime' && resolvedWsUrl ? 'realtime' : 'legacy';
        const startConfig = {
          type: 'start',
          firstMessage,
          scriptId,
          agentId,
          collectionIds: Array.isArray(collectionIds) && collectionIds.length > 0 ? collectionIds : null,
          voiceId,
          backendMode: selectedBackendMode,
          useRealtime: selectedBackendMode === 'realtime'
        };
        if (typeof agentName === 'string' && agentName.trim().length > 0) {
          startConfig.agentName = agentName.trim();
        }
        if (typeof systemPrompt === 'string' && systemPrompt.trim().length > 0) {
          startConfig.systemPrompt = systemPrompt;
        }
        const placedByUserId = getCurrentUserIdForCalls();
        if (placedByUserId) startConfig.placedByUserId = placedByUserId;
        if (extraStart && typeof extraStart === 'object') Object.assign(startConfig, extraStart);
        wsRef.current.send(JSON.stringify(startConfig));
      };

      wsRef.current.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'status') {
          // Handle status for PTT, Kokoro PTT, and Kokoro VAD modes
          if ((backendMode === 'ptt' || backendMode === 'kokoro' || backendMode === 'kokoro-vad' || backendMode === 'kokoro-vad-ws' || backendMode === 'kokoro-ws') && data.state) {
            if (data.state === 'speaking') {
              setIsAssistantSpeaking(true);
            } else if (data.state === 'ready' || data.state === 'listening') {
              setIsAssistantSpeaking(false);
            }
          }
          if (data.state) {
            updateStatus(data.state);
          }
          return;
        }
        
        if (data.type === 'ready') {
          updateStatus('listening');
          setIsRecording(true);
        }

        if (data.type === 'transcript') {
          if (data.role === 'user') {
            // Merge ongoing partials into a single user message until assistant responds
            upsertUserTranscript(data.text);
            if (backendMode !== 'ptt') {
              updateStatus('processing');
            }
          } else {
            // Assistant message marks the end of user utterance
            finalizeUserUtterance();
            addTranscript(data.role, data.text);
            if (backendMode !== 'ptt') {
              updateStatus('speaking');
            }
          }
        }

        if (data.type === 'audio') {
          const audioData = base64ToArrayBuffer(data.audio);
          audioQueueRef.current.push(audioData);
          if (!isPlayingRef.current) {
            playAudioQueue();
          }
        }

        if (data.type === 'metrics') {
          updateMetrics(data.latency);
        }

        if (data.type === 'error') {
          console.error('Agent error:', data.message);
          alert('Error: ' + data.message);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        console.error('❌ WebSocket error details:', {
          type: error.type,
          target: error.target,
          url: error.target?.url,
          readyState: error.target?.readyState,
          readyStateText: error.target?.readyState === 0 ? 'CONNECTING' : 
                          error.target?.readyState === 1 ? 'OPEN' :
                          error.target?.readyState === 2 ? 'CLOSING' :
                          error.target?.readyState === 3 ? 'CLOSED' : 'UNKNOWN',
          bufferedAmount: error.target?.bufferedAmount,
          protocol: error.target?.protocol,
          extensions: error.target?.extensions
        });
        console.error('❌ WebSocket URL attempted:', resolvedWsUrl);
        updateStatus('disconnected');
        stop();
      };

      wsRef.current.onclose = (event) => {
        console.log('🔌 WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          url: resolvedWsUrl
        });
        updateStatus('disconnected');
        stop();
      };

      // Send audio data to server
      audioProcessorRef.current.port.onmessage = (event) => {
        if (backendMode === 'realtime') {
          if (peerRef.current && peerRef.current.connectionState === 'connected') {
            // TODO: send PCM via data channel if needed
          }
        } else if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const pcmData = event.data;
          const base64Audio = arrayBufferToBase64(pcmData);
          wsRef.current.send(JSON.stringify({
            type: 'audio',
            audio: base64Audio
          }));
        }
      };

    } catch (error) {
      console.error('Start error:', error);
      alert('Failed to start: ' + error.message);
      updateStatus('disconnected');
      stop();
    }
  };

  // Start Push-to-Talk mode
  const startPtt = async () => {
    try {
      updateStatus('connecting');

      // Get microphone access
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Setup audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ 
        sampleRate: 16000 
      });
      const source = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
      
      // Setup analyser for visualization
      if (showVisualizer) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        source.connect(analyserRef.current);
        startVisualization();
      }

      // Setup audio processor for PCM16 conversion
      const workletCode = `
        class PCMProcessor extends AudioWorkletProcessor {
          process(inputs) {
            const input = inputs[0];
            if (input.length > 0) {
              const channelData = input[0];
              const pcm16 = new Int16Array(channelData.length);
              for (let i = 0; i < channelData.length; i++) {
                const s = Math.max(-1, Math.min(1, channelData[i]));
                pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
              }
              this.port.postMessage(pcm16.buffer);
            }
            return true;
          }
        }
        registerProcessor('pcm-processor', PCMProcessor);
      `;
      
      const blob = new Blob([workletCode], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);
      
      await audioContextRef.current.audioWorklet.addModule(workletUrl);
      audioProcessorRef.current = new AudioWorkletNode(audioContextRef.current, 'pcm-processor');
      source.connect(audioProcessorRef.current);

      // Connect WebSocket
      console.log('[BrowserVoiceWidget PTT] Connecting to WebSocket:', resolvedWsUrl);
      wsRef.current = new WebSocket(resolvedWsUrl);

      wsRef.current.onopen = () => {
        console.log('✅ PTT WebSocket connected to:', resolvedWsUrl);
        updateStatus('ready');

        // Send start configuration
        const startConfig = {
          type: 'start',
          firstMessage,
          scriptId,
          agentId,
          collectionIds: Array.isArray(collectionIds) && collectionIds.length > 0 ? collectionIds : null,
          voiceId,
          backendMode: 'ptt'
        };
        if (typeof agentName === 'string' && agentName.trim().length > 0) {
          startConfig.agentName = agentName.trim();
        }
        if (typeof systemPrompt === 'string' && systemPrompt.trim().length > 0) {
          startConfig.systemPrompt = systemPrompt;
        }
        const placedByUserIdPtt = getCurrentUserIdForCalls();
        if (placedByUserIdPtt) startConfig.placedByUserId = placedByUserIdPtt;
        if (extraStart && typeof extraStart === 'object') Object.assign(startConfig, extraStart);
        wsRef.current.send(JSON.stringify(startConfig));
      };

      wsRef.current.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'ready') {
          updateStatus('ready');
          setIsRecording(true); // Session is active, but not continuously recording
        }

        if (data.type === 'status') {
          // Handle status updates from backend
          if (data.state === 'speaking') {
            setIsAssistantSpeaking(true);
            updateStatus('speaking');
          } else if (data.state === 'listening' || data.state === 'ready') {
            setIsAssistantSpeaking(false);
            updateStatus(data.state);
          } else {
            updateStatus(data.state);
          }
        }

        if (data.type === 'recording_started') {
          setIsPttRecording(true);
          isPttRecordingRef.current = true;
          updateStatus('listening');
        }

        if (data.type === 'recording_stopped') {
          setIsPttRecording(false);
          isPttRecordingRef.current = false;
          updateStatus('processing');
        }

        if (data.type === 'transcript') {
          addTranscript(data.role, data.text);
          if (onTranscript) onTranscript(data.role, data.text);
          if (data.role === 'assistant') {
            updateStatus('speaking');
          }
        }

        if (data.type === 'audio') {
          // Queue audio chunk for sequential playback
          try {
            const audioData = base64ToArrayBuffer(data.audio);
            pttAudioQueueRef.current.push(audioData);
            playPttAudioQueue(); // Start playing if not already
          } catch (err) {
            console.error('PTT audio queue error:', err);
          }
        }

        if (data.type === 'metrics') {
          setMetrics(data.latency || {});
          if (onMetrics) onMetrics(data.latency);
        }

        if (data.type === 'error') {
          console.error('PTT error:', data.error || data.message);
          alert('Error: ' + (data.error?.message || data.message || 'Unknown error'));
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('❌ PTT WebSocket error:', error);
        console.error('❌ PTT WebSocket error details:', {
          url: error.target?.url,
          readyState: error.target?.readyState,
          readyStateText: error.target?.readyState === 0 ? 'CONNECTING' : 
                          error.target?.readyState === 1 ? 'OPEN' :
                          error.target?.readyState === 2 ? 'CLOSING' :
                          error.target?.readyState === 3 ? 'CLOSED' : 'UNKNOWN',
        });
        updateStatus('disconnected');
      };

      wsRef.current.onclose = (event) => {
        console.log('🔌 PTT WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        updateStatus('disconnected');
        setIsRecording(false);
        setIsPttRecording(false);
      };

      // Setup audio processor to send audio when recording
      audioProcessorRef.current.port.onmessage = (event) => {
        if (wsRef.current?.readyState === WebSocket.OPEN && isPttRecordingRef.current) {
          const pcmData = event.data;
          const base64Audio = arrayBufferToBase64(pcmData);
          wsRef.current.send(JSON.stringify({
            type: 'audio',
            audio: base64Audio
          }));
        }
      };

    } catch (error) {
      console.error('PTT start error:', error);
      alert('Failed to start PTT: ' + error.message);
      updateStatus('disconnected');
      stopPtt();
    }
  };

  // Stop Push-to-Talk mode
  const stopPtt = async () => {
    setIsRecording(false);
    setIsPttRecording(false);
    isPttRecordingRef.current = false;
    setIsAssistantSpeaking(false);
    
    // Stop current recording if active
    if (isPttRecording && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'stop_recording' }));
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    // Stop audio processor
    if (audioProcessorRef.current) {
      audioProcessorRef.current.disconnect();
      audioProcessorRef.current = null;
    }
    
    // Close audio context
    if (audioContextRef.current) {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    
    // Stop visualization
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Clear PTT audio queue and close playback context
    pttAudioQueueRef.current = [];
    pttIsPlayingRef.current = false;
    if (pttPlaybackCtxRef.current && pttPlaybackCtxRef.current.state !== 'closed') {
      try {
        await pttPlaybackCtxRef.current.close();
      } catch {}
      pttPlaybackCtxRef.current = null;
    }
    
    updateStatus('disconnected');
  };

  // Push-to-talk button handlers
  const handlePttButtonPress = () => {
    const readyForPress = (status === 'ready' || status === 'listening');
    if ((backendMode === 'ptt' || backendMode === 'kokoro' || backendMode === 'kokoro-ws') && (!isRecording || isAssistantSpeaking || !readyForPress)) {
      return;
    }
    if (wsRef.current?.readyState === WebSocket.OPEN && !isPttRecording) {
      wsRef.current.send(JSON.stringify({ type: 'start_recording' }));
    }
  };

  const handlePttButtonRelease = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && isPttRecording) {
      wsRef.current.send(JSON.stringify({ type: 'stop_recording' }));
    }
  };

  // Stop voice agent
  const startRealtime = async () => {
    try {
      updateStatus('connecting');
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // For Realtime mode, connect to our backend proxy endpoint
      // The backend handles OpenAI authentication with proper headers
      // Send session initialization data as first message
      console.log('Connecting to Realtime WebSocket proxy:', resolvedWsUrl);
      
      const realtimeWs = new WebSocket(resolvedWsUrl);

      realtimeWsRef.current = realtimeWs;

      // Setup audio context for processing incoming audio
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      audioContextRef.current = audioCtx;
      const audioQueue = [];

      realtimeWs.onopen = () => {
        console.log('✅ Realtime WebSocket connected to backend proxy');
        
        // Send session initialization to backend proxy
        // Backend will handle OpenAI connection and authentication
        const rtStartPayload = {
          type: 'start',
          scriptId,
          collectionIds: Array.isArray(collectionIds) && collectionIds.length > 0 ? collectionIds : null,
          agentName,
          systemPrompt: systemPrompt || `You are ${agentName || 'Maya'}, a helpful AI voice assistant.`,
          firstMessage,
          backendMode: 'realtime',
          useRealtime: true
        };
        const placedByUserIdRt = getCurrentUserIdForCalls();
        if (placedByUserIdRt) rtStartPayload.placedByUserId = placedByUserIdRt;
        realtimeWs.send(JSON.stringify(rtStartPayload));
        
        updateStatus('ready');
        setIsRecording(true);
      };

      realtimeWs.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          console.log('Realtime proxy message:', msg.type);
          
          // Handle ready signal from backend
          if (msg.type === 'ready') {
            console.log('✅ Realtime agent ready:', msg.message);
            updateStatus('ready');
            return;
          }
          
          // Handle user transcript from backend (backend transcribes audio)
          if (msg.type === 'transcript' && msg.role === 'user') {
            const userText = msg.text || '';
            if (userText) {
              setTranscript(prev => [...prev, { role: 'user', text: userText, timestamp: msg.timestamp || Date.now() }]);
              if (onTranscript) onTranscript('user', userText);
              updateStatus('processing');
            }
          }
          
          // Handle assistant transcript from backend
          if (msg.type === 'transcript' && msg.role === 'assistant') {
            const assistantText = msg.text || '';
            if (assistantText) {
              setTranscript(prev => [...prev, { role: 'assistant', text: assistantText, timestamp: msg.timestamp || Date.now() }]);
              if (onTranscript) onTranscript('assistant', assistantText);
              updateStatus('speaking');
            }
          }
          
          // Handle audio chunks from backend (backend forwards OpenAI audio)
          if (msg.type === 'audio') {
            const audioBase64 = msg.audio || '';
            if (audioBase64) {
              // Decode base64 audio and queue for playback
              const audioData = base64ToArrayBuffer(audioBase64);
              audioQueue.push(audioData);
              playRealtimeAudio(audioCtx, audioQueue);
              updateStatus('speaking');
            }
          }
          
          // Handle metrics from backend
          if (msg.type === 'metrics' && msg.latency) {
            if (onMetrics) {
              onMetrics({
                detection: msg.latency.detection || 0,
                kb: msg.latency.kb || 0,
                gpt: msg.latency.gpt || 0,
                tts: msg.latency.tts || 0,
                total: msg.latency.total || 0
              });
            }
            updateStatus('listening');
          }
          
          // Handle errors
          if (msg.type === 'error') {
            console.error('Realtime proxy error:', msg.error || msg.message);
            updateStatus('disconnected');
          }
        } catch (err) {
          console.warn('Realtime message parse error:', err, event.data);
        }
      };

      realtimeWs.onerror = (error) => {
        console.error('Realtime WebSocket error:', error);
        console.error('WebSocket state:', realtimeWs.readyState);
        updateStatus('disconnected');
      };

      realtimeWs.onclose = (event) => {
        console.log('Realtime WebSocket closed', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        updateStatus('disconnected');
      };

      // Setup audio processor to send mic audio to Realtime API
      const workletCode = `
        class RealtimePCMProcessor extends AudioWorkletProcessor {
          process(inputs) {
            const input = inputs[0];
            if (input.length > 0) {
              const channelData = input[0];
              const pcm16 = new Int16Array(channelData.length);
              for (let i = 0; i < channelData.length; i++) {
                const s = Math.max(-1, Math.min(1, channelData[i]));
                pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
              }
              this.port.postMessage(pcm16.buffer);
            }
            return true;
          }
        }
        registerProcessor('realtime-pcm-processor', RealtimePCMProcessor);
      `;
      
      const blob = new Blob([workletCode], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);
      
      await audioContextRef.current.audioWorklet.addModule(workletUrl);
      const processor = new AudioWorkletNode(audioContextRef.current, 'realtime-pcm-processor');
      const source = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
      source.connect(processor);

      // Send audio chunks to backend proxy
      // Backend handles transcription and forwarding to OpenAI
      processor.port.onmessage = (event) => {
        if (realtimeWs.readyState === WebSocket.OPEN) {
          const pcmData = event.data;
          const base64Audio = arrayBufferToBase64(pcmData);
          
          // Send audio to backend proxy (backend handles OpenAI communication)
          realtimeWs.send(JSON.stringify({
            type: 'audio',
            audio: base64Audio
          }));
        }
      };

      audioProcessorRef.current = processor;
    } catch (error) {
      console.error('Realtime start error:', error);
      alert('Failed to start realtime: ' + error.message);
      updateStatus('disconnected');
      await stopRealtime();
    }
  };

  const start = async () => {
    if (backendMode === 'realtime') {
      await startRealtime();
    } else if (backendMode === 'ptt' || backendMode === 'kokoro' || backendMode === 'kokoro-ws') {
      await startPtt(); // Kokoro PTT uses the same PTT flow
    } else {
      await startLegacy(); // Legacy, V2, Kokoro VAD, and Kokoro VAD WS use open mic
    }
  };

  const stop = () => {
    if (backendMode === 'realtime') {
      stopRealtime();
    } else if (backendMode === 'ptt' || backendMode === 'kokoro' || backendMode === 'kokoro-ws') {
      stopPtt();
      updateStatus('disconnected');
      return;
    }
    setIsRecording(false);
    setIsAssistantSpeaking(false);

    // Stop visualization
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ type: 'stop' }));
      wsRef.current.close();
      wsRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Clear audio queue
    audioQueueRef.current = [];
    isPlayingRef.current = false;

    updateStatus('disconnected');
  };

  // Visualization
  const startVisualization = () => {
    if (!canvasRef.current || !analyserRef.current) return;

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);

      analyserRef.current.getByteFrequencyData(dataArray);

      canvasCtx.fillStyle = '#1e293b';
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height;
        
        const gradient = canvasCtx.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
        gradient.addColorStop(0, '#3b82f6');
        gradient.addColorStop(1, '#8b5cf6');
        
        canvasCtx.fillStyle = gradient;
        canvasCtx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        
        x += barWidth + 1;
      }
    };

    draw();
  };

  // Auto start on mount
  useEffect(() => {
    if (autoStart) {
      start();
    }

    return () => {
      stop();
    };
  }, [autoStart]);

  // Status indicator colors
  const statusMeta = {
    disconnected: { label: 'Not connected', dot: 'bg-slate-300', text: 'text-slate-500' },
    connecting: { label: 'Connecting…', dot: 'bg-amber-400', text: 'text-amber-600' },
    ready: { label: 'Ready', dot: 'bg-teal-500', text: 'text-teal-700' },
    listening: { label: 'Listening…', dot: 'bg-teal-500', text: 'text-teal-700' },
    processing: { label: 'Thinking…', dot: 'bg-teal-500', text: 'text-teal-700' },
    speaking: { label: 'Speaking…', dot: 'bg-teal-500', text: 'text-teal-700' },
  };
  const meta = statusMeta[status] || statusMeta.disconnected;
  const isPtt = backendMode === 'ptt' || backendMode === 'kokoro' || backendMode === 'kokoro-ws';
  const pttReadyState = status === 'ready' || status === 'listening';
  const pttButtonDisabled = !isRecording || isAssistantSpeaking || (!isPttRecording && !pttReadyState);
  const speaking = status === 'speaking' || isAssistantSpeaking;
  const listening = isRecording && !speaking;

  const MicGlyph = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
          <MicGlyph size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-slate-800">Voice Agent</div>
          <div className="flex items-center gap-1.5 text-[12px]">
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot} ${(listening || speaking) ? 'animate-pulse' : ''}`} />
            <span className={meta.text}>{meta.label}</span>
          </div>
        </div>
      </div>

      {/* Mic orb + controls */}
      <div className="flex flex-col items-center justify-center gap-4 px-5 pt-7 pb-5">
        <button
          type="button"
          onClick={isPtt ? undefined : () => (isRecording ? stop() : start())}
          onMouseDown={isPtt ? () => { if (!(!isPttRecording && pttButtonDisabled)) handlePttButtonPress(); } : undefined}
          onMouseUp={isPtt ? handlePttButtonRelease : undefined}
          onTouchStart={isPtt ? (e) => { e.preventDefault(); if (!(!isPttRecording && pttButtonDisabled)) handlePttButtonPress(); } : undefined}
          onTouchEnd={isPtt ? (e) => { e.preventDefault(); handlePttButtonRelease(); } : undefined}
          className="relative flex h-28 w-28 items-center justify-center rounded-full outline-none"
          aria-label="microphone"
        >
          {(listening || isPttRecording) && <span className="absolute inset-0 animate-ping rounded-full bg-teal-400/30" />}
          {speaking && <span className="absolute inset-1 animate-pulse rounded-full bg-teal-400/25" />}
          <span className={`relative flex h-24 w-24 items-center justify-center rounded-full text-white shadow-lg transition-colors ${
            isPttRecording ? 'bg-rose-500' : (speaking || isRecording) ? 'bg-teal-600' : 'bg-slate-300 hover:bg-slate-400'
          }`}>
            <MicGlyph size={34} />
          </span>
        </button>

        <p className="h-5 text-[13px] text-slate-500">
          {!isRecording
            ? (isPtt ? 'Start a session, then hold to speak' : 'Tap the mic to start talking')
            : speaking ? 'Agent is speaking…'
            : isPtt ? (isPttRecording ? 'Recording — release to send' : 'Hold the mic to speak')
            : 'Listening — just talk'}
        </p>

        {!isRecording ? (
          <button onClick={start} className="rounded-xl bg-teal-600 px-6 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-teal-700">
            Start session
          </button>
        ) : (
          <button onClick={stop} className="rounded-xl border border-slate-200 px-6 py-2 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-slate-50">
            End session
          </button>
        )}
      </div>

      {showVisualizer && (
        <div className="px-5">
          <canvas ref={canvasRef} width={600} height={48} className="h-12 w-full rounded-xl bg-slate-900" />
        </div>
      )}

      {showMetrics && (
        <div className="grid grid-cols-4 gap-2 px-5 pb-1">
          {[['STT', metrics.detection], ['LLM', metrics.gpt], ['TTS', metrics.tts], ['Total', metrics.total]].map(([k, v], i) => (
            <div key={k} className={`rounded-xl px-3 py-2 ${i === 3 ? 'bg-teal-50' : 'bg-slate-50'}`}>
              <div className={`text-[10px] font-semibold uppercase tracking-wide ${i === 3 ? 'text-teal-600' : 'text-slate-400'}`}>{k}</div>
              <div className={`text-[15px] font-bold ${i === 3 ? 'text-teal-700' : 'text-slate-700'}`}>{(v && typeof v === 'object') ? (v.total ?? JSON.stringify(v)) : (v || '—')}<span className="text-[10px] font-medium text-slate-400">ms</span></div>
            </div>
          ))}
        </div>
      )}

      {showTranscript && (
        <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto border-t border-slate-100 bg-slate-50/40 px-4 py-4">
          {transcript.length === 0 ? (
            <div className="grid h-full place-items-center text-[13px] text-slate-400">Your conversation will appear here.</div>
          ) : (
            transcript.map((entry, index) => {
              const me = entry.role === 'user';
              return (
                <div key={index} className={`flex ${me ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug shadow-sm ${
                    me ? 'bg-teal-600 text-white' : 'border border-slate-200 bg-white text-slate-700'
                  }`}>
                    {typeof entry.text === 'string' ? entry.text : (entry.text?.text ?? JSON.stringify(entry.text))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default BrowserVoiceWidget;

